import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Users, 
  UserPlus, 
  Settings, 
  Trash2, 
  Loader2,
  Crown,
  Shield,
  Eye,
  User
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ProjectMemberService, WorkspaceMemberForProject } from "@/services/project-member-service";
import { V3ProjectRole } from "@/integrations/supabase/v3-types";

interface ProjectMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
}

export const ProjectMembersModal: React.FC<ProjectMembersModalProps> = ({
  isOpen,
  onClose,
  projectId,
  projectName
}) => {
  const { toast } = useToast();
  
  // State
  const [loading, setLoading] = useState(false);
  const [projectMembers, setProjectMembers] = useState<any[]>([]);
  const [availableMembers, setAvailableMembers] = useState<WorkspaceMemberForProject[]>([]);
  const [selectedMember, setSelectedMember] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<V3ProjectRole>('project_member');
  const [isProjectAdmin, setIsProjectAdmin] = useState(false);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadProjectData();
    }
  }, [isOpen, projectId]);

  const loadProjectData = async () => {
    try {
      setLoading(true);
      
      // Load project members
      const members = await ProjectMemberService.getProjectMembers(projectId);
      setProjectMembers(members);
      
      // Load available workspace members
      const available = await ProjectMemberService.getAvailableWorkspaceMembers(projectId);
      setAvailableMembers(available);
      
      // Check if current user is project admin
      const isAdmin = await ProjectMemberService.isProjectAdmin(projectId);
      setIsProjectAdmin(isAdmin);
      
    } catch (error) {
      console.error('Error loading project data:', error);
      toast({
        title: "Error",
        description: "Failed to load project members",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAssignMember = async () => {
    if (!selectedMember || !selectedRole) return;

    try {
      setLoading(true);
      
      const success = await ProjectMemberService.assignMemberToProject(
        projectId, 
        selectedMember, 
        selectedRole
      );

      if (success) {
        toast({
          title: "Success",
          description: "Member assigned to project successfully"
        });
        
        // Reset form
        setSelectedMember('');
        setSelectedRole('project_member');
        
        // Reload data
        await loadProjectData();
      } else {
        throw new Error('Failed to assign member');
      }
    } catch (error) {
      console.error('Error assigning member:', error);
      toast({
        title: "Error",
        description: "Failed to assign member to project",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (memberId: string, newRole: V3ProjectRole) => {
    try {
      setLoading(true);
      
      const success = await ProjectMemberService.updateProjectMemberRole(memberId, newRole);

      if (success) {
        toast({
          title: "Success",
          description: "Member role updated successfully"
        });
        
        // Reload data
        await loadProjectData();
      } else {
        throw new Error('Failed to update role');
      }
    } catch (error) {
      console.error('Error updating role:', error);
      toast({
        title: "Error",
        description: "Failed to update member role",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      setLoading(true);
      
      const success = await ProjectMemberService.removeProjectMember(memberId);

      if (success) {
        toast({
          title: "Success",
          description: "Member removed from project successfully"
        });
        
        // Reload data
        await loadProjectData();
      } else {
        throw new Error('Failed to remove member');
      }
    } catch (error) {
      console.error('Error removing member:', error);
      toast({
        title: "Error",
        description: "Failed to remove member from project",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadge = (role: V3ProjectRole) => {
    switch (role) {
      case 'project_admin':
        return <Badge variant="destructive" className="gap-1"><Crown className="w-3 h-3" />Admin</Badge>;
      case 'project_lead':
        return <Badge variant="default" className="gap-1"><Shield className="w-3 h-3" />Lead</Badge>;
      case 'project_member':
        return <Badge variant="secondary" className="gap-1"><User className="w-3 h-3" />Member</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const getRoleDescription = (role: V3ProjectRole) => {
    switch (role) {
      case 'project_admin':
        return 'Full project control, can manage members and settings';
      case 'project_lead':
        return 'Can view and upload data but not configure';
      case 'project_member':
        return 'Can view data only';
      default:
        return 'Standard project access';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Project Members - {projectName}
          </DialogTitle>
          <DialogDescription>
            Manage team members and their roles for this project.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current Project Members */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Current Members</CardTitle>
              <CardDescription>
                Team members assigned to this project and their roles.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                  <span className="text-muted-foreground">Loading members...</span>
                </div>
              ) : projectMembers.length === 0 ? (
                <div className="text-center py-6">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No members assigned yet</p>
                  <p className="text-sm text-muted-foreground">Add team members to get started</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {projectMembers.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">
                            {member.user_display_name || member.user_email}
                          </span>
                          {getRoleBadge(member.role)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {member.user_email}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {getRoleDescription(member.role)}
                        </div>
                      </div>
                      {isProjectAdmin && (
                        <div className="flex items-center gap-2">
                          <Select
                            value={member.role}
                            onValueChange={(value: V3ProjectRole) => handleUpdateRole(member.id, value)}
                            disabled={loading}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="project_admin">Admin</SelectItem>
                              <SelectItem value="project_lead">Lead</SelectItem>
                              <SelectItem value="project_member">Member</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRemoveMember(member.id)}
                            className="gap-1 text-destructive hover:text-destructive"
                            disabled={loading}
                          >
                            <Trash2 className="w-3 h-3" />
                            Remove
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Add New Member */}
          {isProjectAdmin && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  Add Member
                </CardTitle>
                <CardDescription>
                  Assign workspace members to this project with specific roles.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="member-select">Select Member</Label>
                    <Select value={selectedMember} onValueChange={setSelectedMember} disabled={loading}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a member" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableMembers
                          .filter(member => !member.is_already_assigned)
                          .map((member) => (
                            <SelectItem key={member.user_id} value={member.user_id}>
                              <div className="flex items-center gap-2">
                                <span>{member.user_display_name || member.user_email}</span>
                                <Badge variant="outline" className="text-xs">
                                  {member.workspace_role.replace('workspace_', '')}
                                </Badge>
                              </div>
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="role-select">Assign Role</Label>
                    <Select value={selectedRole} onValueChange={(value: V3ProjectRole) => setSelectedRole(value)} disabled={loading}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="project_admin">
                          <div className="flex items-center gap-2">
                            {getRoleBadge('project_admin')}
                            <span className="text-sm">Full project control</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="project_lead">
                          <div className="flex items-center gap-2">
                            {getRoleBadge('project_lead')}
                            <span className="text-sm">View & upload data</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="project_member">
                          <div className="flex items-center gap-2">
                            {getRoleBadge('project_member')}
                            <span className="text-sm">View data only</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button 
                  onClick={handleAssignMember}
                  disabled={loading || !selectedMember || !selectedRole}
                  className="gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Assigning...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4" />
                      Assign Member
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {!isProjectAdmin && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Access Control</CardTitle>
                <CardDescription>
                  Only project administrators can manage team members and roles.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-4">
                  <Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">You need project admin permissions to manage members</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}; 